import { createRoot, type Root } from "react-dom/client";
import { QrCode } from "./qr";
import "./studio-student-photo.css";

type UploadSession = {
  sessionId: string;
  status: "waiting" | "completed";
  url: string;
  path: string;
  uploadPath: string;
  expiresAt: string;
};

type SessionStatus = {
  sessionId: string;
  status: "waiting" | "completed";
  blockId: string;
  activityKey?: string | null;
  expiresAt: string;
  photo: null | {
    id: string;
    name?: string;
    mimeType: string;
    url: string;
    createdAt: string;
  };
};

type PreparedImage = {
  name: string;
  mimeType: "image/jpeg";
  data: string;
  preview: string;
};

const roots = new Set<Root>();
let scheduled = false;

function isStudentPreviewMode() {
  return Boolean(
    document.querySelector(
      ".interactive-preview-panel > .studio-panel-bar .studio-row button:last-child.active",
    ),
  );
}

function activityKey() {
  return document.querySelector(".interactive-activity-meta h2")?.textContent?.trim() || "activité-interactive";
}

function eligibleQuestion(article: HTMLElement) {
  const block = article.querySelector<HTMLElement>(".studio-student-block");
  if (!block) return false;
  if (block.querySelector('textarea[aria-label="Réponse élève"]')) return true;
  const textInput = block.querySelector<HTMLInputElement>(
    'input[aria-label="Réponse élève"][type="text"]',
  );
  return Boolean(textInput);
}

function statusElement(container: HTMLElement) {
  return container.querySelector<HTMLElement>(".student-photo-response-status");
}

function setStatus(container: HTMLElement, message: string, tone: "" | "error" | "ok" = "") {
  const status = statusElement(container);
  if (!status) return;
  status.className = `student-photo-response-status${tone ? ` ${tone}` : ""}`;
  status.textContent = message;
}

async function createSession(blockId: string): Promise<UploadSession> {
  const response = await fetch("/api/interactive-photo/sessions", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ blockId, activityKey: activityKey() }),
  });
  const body = await response.json();
  if (!response.ok) throw new Error(body.error || "Impossible de préparer l’envoi photo");
  return body as UploadSession;
}

async function sessionStatus(sessionId: string): Promise<SessionStatus> {
  const response = await fetch(`/api/interactive-photo/sessions/${encodeURIComponent(sessionId)}`, {
    cache: "no-store",
  });
  const body = await response.json();
  if (!response.ok) throw new Error(body.error || "Impossible de lire le statut de la photo");
  return body as SessionStatus;
}

async function jpegExifOrientation(file: File): Promise<number> {
  if (file.type.toLowerCase() !== "image/jpeg") return 1;
  try {
    const buffer=await file.arrayBuffer(), view=new DataView(buffer);
    if(view.byteLength<12||view.getUint16(0,false)!==0xffd8)return 1;
    let offset=2;
    while(offset+4<=view.byteLength){
      const marker=view.getUint16(offset,false);
      if((marker&0xff00)!==0xff00||marker===0xffda||marker===0xffd9)break;
      const length=view.getUint16(offset+2,false);
      if(length<2||offset+2+length>view.byteLength)break;
      if(marker===0xffe1&&length>=10&&view.getUint32(offset+4,false)===0x45786966){
        const tiff=offset+10;
        if(tiff+8>view.byteLength)return 1;
        const endian=view.getUint16(tiff,false),little=endian===0x4949;
        if(!little&&endian!==0x4d4d)return 1;
        const ifd=tiff+view.getUint32(tiff+4,little);
        if(ifd+2>view.byteLength)return 1;
        const count=view.getUint16(ifd,little);
        for(let i=0;i<count;i++){
          const entry=ifd+2+i*12;
          if(entry+12>view.byteLength)break;
          if(view.getUint16(entry,little)===0x0112){
            const orientation=view.getUint16(entry+8,little);
            return orientation>=1&&orientation<=8?orientation:1;
          }
        }
        return 1;
      }
      offset+=2+length;
    }
  } catch {}
  return 1;
}

function encodePreparedImage(file: File, source: CanvasImageSource, sourceWidth: number, sourceHeight: number, orientation: number): PreparedImage {
  const swapped=orientation>=5&&orientation<=8;
  const orientedWidth=swapped?sourceHeight:sourceWidth, orientedHeight=swapped?sourceWidth:sourceHeight;
  const scale=Math.min(1,1800/Math.max(orientedWidth,orientedHeight));
  const canvas=document.createElement("canvas");
  canvas.width=Math.max(1,Math.round(orientedWidth*scale));
  canvas.height=Math.max(1,Math.round(orientedHeight*scale));
  const context=canvas.getContext("2d");
  if(!context)throw new Error("Canvas indisponible");
  context.scale(scale,scale);
  switch(orientation){
    case 2:context.transform(-1,0,0,1,sourceWidth,0);break;
    case 3:context.transform(-1,0,0,-1,sourceWidth,sourceHeight);break;
    case 4:context.transform(1,0,0,-1,0,sourceHeight);break;
    case 5:context.transform(0,1,1,0,0,0);break;
    case 6:context.transform(0,1,-1,0,sourceHeight,0);break;
    case 7:context.transform(0,-1,-1,0,sourceHeight,sourceWidth);break;
    case 8:context.transform(0,-1,1,0,0,sourceWidth);break;
  }
  context.drawImage(source,0,0,sourceWidth,sourceHeight);
  const preview=canvas.toDataURL("image/jpeg",0.86),data=preview.split(",")[1]||"";
  if(!data)throw new Error("Image vide");
  return {name:`${file.name.replace(/\.[^.]+$/,"")||"photo"}.jpg`,mimeType:"image/jpeg",data,preview};
}

async function prepareImage(file: File): Promise<PreparedImage> {
  if (!file.type.startsWith("image/")) throw new Error("Choisis une image.");
  const orientation=await jpegExifOrientation(file);
  if("createImageBitmap" in window){
    try{
      const bitmap=await createImageBitmap(file,{imageOrientation:"none"});
      try{return encodePreparedImage(file,bitmap,bitmap.width,bitmap.height,orientation);}
      finally{bitmap.close();}
    }catch{}
  }
  const objectUrl=URL.createObjectURL(file);
  try{
    const image=await new Promise<HTMLImageElement>((resolve,reject)=>{
      const element=new Image();
      element.onload=()=>resolve(element);
      element.onerror=()=>reject(new Error("Image illisible"));
      element.src=objectUrl;
    });
    return encodePreparedImage(file,image,image.naturalWidth,image.naturalHeight,1);
  }finally{URL.revokeObjectURL(objectUrl);}
}

async function uploadPrepared(session: UploadSession, prepared: PreparedImage) {
  const response = await fetch(session.uploadPath, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      name: prepared.name,
      mimeType: prepared.mimeType,
      data: prepared.data,
    }),
  });
  const body = await response.json();
  if (!response.ok) throw new Error(body.error || "Envoi impossible");
}

function showPhoto(container: HTMLElement, photo: NonNullable<SessionStatus["photo"]>) {
  const image = container.querySelector<HTMLImageElement>(".student-photo-response-preview");
  if (!image) return;
  image.src = `${photo.url}?v=${encodeURIComponent(photo.id)}`;
  image.alt = photo.name || "Photo envoyée";
  image.classList.add("visible");
  setStatus(container, "Photo reçue ✓", "ok");
}

function closeQrDialog(backdrop: HTMLElement, root: Root, timer?: number) {
  if (timer) window.clearInterval(timer);
  root.unmount();
  roots.delete(root);
  backdrop.remove();
}

function openQrDialog(session: UploadSession, container: HTMLElement) {
  const backdrop = document.createElement("div");
  backdrop.className = "student-photo-qr-backdrop";
  const dialog = document.createElement("section");
  dialog.className = "student-photo-qr-dialog";
  dialog.setAttribute("role", "dialog");
  dialog.setAttribute("aria-modal", "true");
  dialog.innerHTML = `
    <h2>Prendre la photo avec un téléphone</h2>
    <p>Scanne ce QR code. La page du téléphone ouvrira directement la caméra.</p>
    <div class="student-photo-qr-code"></div>
    <code></code>
    <div class="student-photo-qr-state">En attente de la photo…</div>
    <button type="button">Fermer</button>
  `;
  backdrop.append(dialog);
  document.body.append(backdrop);

  const qrMount = dialog.querySelector<HTMLElement>(".student-photo-qr-code")!;
  const code = dialog.querySelector<HTMLElement>("code")!;
  const state = dialog.querySelector<HTMLElement>(".student-photo-qr-state")!;
  const close = dialog.querySelector<HTMLButtonElement>("button")!;
  code.textContent = session.url;

  const root = createRoot(qrMount);
  roots.add(root);
  root.render(<QrCode value={session.url} size={230} title="QR code pour envoyer la photo" />);

  let done = false;
  const check = async () => {
    if (done) return;
    try {
      const current = await sessionStatus(session.sessionId);
      if (current.status === "completed" && current.photo) {
        done = true;
        state.className = "student-photo-qr-state ok";
        state.textContent = "Photo reçue ✓";
        showPhoto(container, current.photo);
      } else if (Date.parse(current.expiresAt) <= Date.now()) {
        done = true;
        state.className = "student-photo-qr-state error";
        state.textContent = "QR expiré. Ferme cette fenêtre et génère-en un nouveau.";
      }
    } catch (error) {
      state.className = "student-photo-qr-state error";
      state.textContent = error instanceof Error ? error.message : "Impossible de vérifier la photo";
    }
  };
  void check();
  const timer = window.setInterval(() => void check(), 1300);
  close.onclick = () => closeQrDialog(backdrop, root, timer);
  backdrop.addEventListener("click", (event) => {
    if (event.target === backdrop) closeQrDialog(backdrop, root, timer);
  });
}

function enhanceQuestion(article: HTMLElement) {
  if (article.querySelector(".student-photo-response-tools")) return;
  if (!eligibleQuestion(article)) return;
  const blockId = article.dataset.blockId;
  const studentBlock = article.querySelector<HTMLElement>(".studio-student-block");
  if (!blockId || !studentBlock) return;

  const container = document.createElement("div");
  container.className = "student-photo-response-tools";
  container.innerHTML = `
    <span class="student-photo-response-title">Ajouter un schéma ou une photo</span>
    <div class="student-photo-response-actions">
      <button type="button" data-photo-direct>📷 Prendre / importer une photo</button>
      <button type="button" data-photo-qr>▦ Utiliser le téléphone par QR</button>
    </div>
    <input data-photo-file type="file" accept="image/*" capture="environment" hidden />
    <div class="student-photo-response-status"></div>
    <img class="student-photo-response-preview" alt="" />
  `;
  studentBlock.append(container);

  const direct = container.querySelector<HTMLButtonElement>("[data-photo-direct]")!;
  const qr = container.querySelector<HTMLButtonElement>("[data-photo-qr]")!;
  const file = container.querySelector<HTMLInputElement>("[data-photo-file]")!;

  direct.onclick = () => file.click();
  file.onchange = async () => {
    const selected = file.files?.[0];
    file.value = "";
    if (!selected) return;
    direct.disabled = true;
    qr.disabled = true;
    setStatus(container, "Préparation de la photo…");
    try {
      const prepared = await prepareImage(selected);
      const session = await createSession(blockId);
      setStatus(container, "Envoi de la photo…");
      await uploadPrepared(session, prepared);
      const current = await sessionStatus(session.sessionId);
      if (current.photo) showPhoto(container, current.photo);
      else setStatus(container, "Photo envoyée, récupération en cours…");
    } catch (error) {
      setStatus(container, error instanceof Error ? error.message : "Envoi impossible", "error");
    } finally {
      direct.disabled = false;
      qr.disabled = false;
    }
  };

  qr.onclick = async () => {
    direct.disabled = true;
    qr.disabled = true;
    setStatus(container, "Création du QR…");
    try {
      const session = await createSession(blockId);
      setStatus(container, "QR prêt. Scanne-le avec le téléphone.");
      openQrDialog(session, container);
    } catch (error) {
      setStatus(container, error instanceof Error ? error.message : "QR indisponible", "error");
    } finally {
      direct.disabled = false;
      qr.disabled = false;
    }
  };
}

function syncPhotoControls() {
  if (!isStudentPreviewMode()) {
    document.querySelectorAll(".student-photo-response-tools").forEach((node) => node.remove());
    return;
  }
  document
    .querySelectorAll<HTMLElement>(".interactive-block-list article[data-block-id]")
    .forEach(enhanceQuestion);
}

function scheduleSync() {
  if (scheduled) return;
  scheduled = true;
  requestAnimationFrame(() => {
    scheduled = false;
    syncPhotoControls();
  });
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", scheduleSync, { once: true });
} else {
  scheduleSync();
}

document.addEventListener("click", scheduleSync, true);
const observer = new MutationObserver(scheduleSync);
observer.observe(document.documentElement, { childList: true, subtree: true, attributes: true, attributeFilter: ["class"] });

window.addEventListener("beforeunload", () => {
  roots.forEach((root) => root.unmount());
  roots.clear();
});
